import { z } from "zod";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

/**
 * Campaign-detail 竞价调整 — Sponsored Products bidding strategy and its bid
 * adjustments (placements, Amazon Business, audiences) beside each
 * placement's performance.
 */

/** Report placements, in the order the tab lists them. Off-Amazon has metrics but no adjustment. */
export const placementKeySchema = z.enum([
  "off_amazon",
  "product_page",
  "rest_of_search",
  "top_of_search",
]);
export type PlacementKey = z.infer<typeof placementKeySchema>;

/** Amazon SP `dynamicBidding.strategy`. */
export const spBiddingStrategySchema = z.enum([
  "LEGACY_FOR_SALES",
  "AUTO_FOR_SALES",
  "MANUAL",
  "RULE_BASED",
]);
export type SpBiddingStrategy = z.infer<typeof spBiddingStrategySchema>;

export const placementMetricsSchema = z.object({
  impressions: z.number(),
  clicks: z.number(),
  spend: z.number(),
  sales: z.number(),
  orders: z.number(),
  /** Ratios are null when their denominator is zero. */
  ctr: z.number().nullable(),
  cpc: z.number().nullable(),
  cvr: z.number().nullable(),
  cpa: z.number().nullable(),
  acos: z.number().nullable(),
  roas: z.number().nullable(),
});
export type PlacementMetrics = z.infer<typeof placementMetricsSchema>;

export const campaignBidAdjustmentsQuerySchema = z
  .object({ ...reportingDateRangeQueryShape })
  .superRefine(validateReportingDateRange);
export type CampaignBidAdjustmentsQuery = z.infer<typeof campaignBidAdjustmentsQuerySchema>;

export const campaignBidAdjustmentsSchema = z.object({
  currency: z.string().nullable(),
  /** Null until the campaign's bidding has been read from Amazon. */
  biddingStrategy: spBiddingStrategySchema.nullable(),
  biddingSyncedAt: z.string().datetime({ offset: true }).nullable(),
  placements: z.array(
    placementMetricsSchema.extend({
      placement: placementKeySchema,
      /** Percent 0–900; null where Amazon takes no adjustment (off-Amazon). */
      adjustment: z.number().nullable(),
    }),
  ),
  amazonBusiness: z.object({
    /** Whether the marketplace offers the Amazon Business placement. */
    available: z.boolean(),
    adjustment: z.number().nullable(),
  }),
  audiences: z.array(
    z.object({
      audienceId: z.string(),
      audienceSegmentType: z.string().nullable(),
      adjustment: z.number(),
    }),
  ),
});
export type CampaignBidAdjustments = z.infer<typeof campaignBidAdjustmentsSchema>;

export const campaignBidAdjustmentsResponseSchema = z.object({
  success: z.literal(true),
  data: campaignBidAdjustmentsSchema,
});
export type CampaignBidAdjustmentsResponse = z.infer<typeof campaignBidAdjustmentsResponseSchema>;

export const placementTrendParamsSchema = z.object({
  id: z.string().uuid(),
  placement: placementKeySchema,
});

export const placementTrendResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    currency: z.string().nullable(),
    points: z.array(placementMetricsSchema.extend({ date: z.string() })),
  }),
});
export type PlacementTrendResponse = z.infer<typeof placementTrendResponseSchema>;

/** Adjustable placements, by our keys (Amazon Business included; off-Amazon takes none). */
export const adjustablePlacementSchema = z.enum([
  "top_of_search",
  "rest_of_search",
  "product_page",
  "amazon_business",
]);
export type AdjustablePlacement = z.infer<typeof adjustablePlacementSchema>;

/** Amazon accepts whole percents 0–900 on every placement. */
export const PLACEMENT_ADJUSTMENT_MAX = 900;
const percent = z.number().int().min(0).max(PLACEMENT_ADJUSTMENT_MAX).optional();

/**
 * 竞价调整 edit: the strategy, some placements' percentages, or both. Fields left
 * out keep their current Amazon value. Rule-based bidding is not offered (Beta,
 * disabled in the reference as well).
 */
export const campaignBiddingUpdateSchema = z
  .object({
    strategy: z.enum(["LEGACY_FOR_SALES", "AUTO_FOR_SALES", "MANUAL"]).optional(),
    placements: z
      .object({
        top_of_search: percent,
        rest_of_search: percent,
        product_page: percent,
        amazon_business: percent,
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine(
    (input) =>
      input.strategy !== undefined ||
      Object.values(input.placements ?? {}).some((value) => value !== undefined),
    "Nothing to change",
  );
export type CampaignBiddingUpdate = z.infer<typeof campaignBiddingUpdateSchema>;
